import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
    PaginationButton,
    buildPaginationPageList,
    calculatePaginationState,
} from '../../utils/paginationUtils'

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200]
const DEFAULT_INITIAL_PAGE_BUTTONS = 7
const MIN_PAGE_BUTTONS = 3
const ESTIMATED_BUTTON_SLOT_WIDTH = 56 // px, button width plus gap allowance
const BUTTON_GAP_PX = 8 // gap-2 value

export type PaginationControlsProps = {
    totalItems: number
    currentPage: number
    onPageChange: (page: number) => void
    pageSize?: number
    defaultPageSize?: number
    onPageSizeChange?: (size: number) => void
    pageSizeOptions?: number[]
    isLoading?: boolean
    maxPageButtonsToShow?: number
    className?: string
    textSizeClassName?: string
    backLabel?: string
    nextLabel?: string
    pageSizeLabel?: string
}

function PaginationControls({
    totalItems,
    currentPage,
    onPageChange,
    pageSize,
    defaultPageSize,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    isLoading = false,
    maxPageButtonsToShow,
    className = '',
    textSizeClassName = 'text-sm',
    backLabel = '< Back',
    nextLabel = 'Next >',
    pageSizeLabel = 'Results per page',
}: PaginationControlsProps): React.JSX.Element {
    const fallbackPageSize = pageSizeOptions[0] ?? DEFAULT_PAGE_SIZE_OPTIONS[0]
    const [internalPageSize, setInternalPageSize] = useState(
        pageSize ?? defaultPageSize ?? fallbackPageSize,
    )
    const [autoMaxButtons, setAutoMaxButtons] = useState(
        maxPageButtonsToShow ?? DEFAULT_INITIAL_PAGE_BUTTONS,
    )
    const navContainerRef = useRef<HTMLDivElement | null>(null)
    const backButtonRef = useRef<HTMLButtonElement | null>(null)
    const nextButtonRef = useRef<HTMLButtonElement | null>(null)

    useEffect(() => {
        if (pageSize !== undefined) {
            setInternalPageSize(pageSize)
        }
    }, [pageSize])

    const effectivePageSize = pageSize ?? internalPageSize
    const resolvedMaxButtons = Math.max(
        MIN_PAGE_BUTTONS,
        maxPageButtonsToShow ?? autoMaxButtons,
    )

    const paginationState = useMemo(
        () =>
            calculatePaginationState(
                totalItems,
                currentPage,
                effectivePageSize,
            ),
        [totalItems, currentPage, effectivePageSize],
    )

    const pageNumberButtons: PaginationButton[] = useMemo(
        () =>
            buildPaginationPageList(
                paginationState.totalPages,
                currentPage,
                resolvedMaxButtons,
            ),
        [paginationState.totalPages, currentPage, resolvedMaxButtons],
    )

    const selectId = useId()

    const handlePageSizeChange = (newSize: number) => {
        if (pageSize === undefined) {
            setInternalPageSize(newSize)
        }
        onPageSizeChange?.(newSize)
    }

    useEffect(() => {
        if (maxPageButtonsToShow !== undefined) {
            return
        }

        const updateMaxButtons = () => {
            const target = navContainerRef.current
            if (!target) return
            const containerWidth = target.clientWidth
            if (!containerWidth) {
                setAutoMaxButtons(MIN_PAGE_BUTTONS)
                return
            }
            const backWidth = backButtonRef.current?.offsetWidth ?? 0
            const nextWidth = nextButtonRef.current?.offsetWidth ?? 0
            const availableWidth = Math.max(
                0,
                containerWidth - backWidth - nextWidth - BUTTON_GAP_PX * 2,
            )
            const calculated = Math.max(
                MIN_PAGE_BUTTONS,
                Math.floor(availableWidth / ESTIMATED_BUTTON_SLOT_WIDTH),
            )
            setAutoMaxButtons((prev) =>
                prev === calculated ? prev : calculated,
            )
        }

        updateMaxButtons()

        let resizeObserver: ResizeObserver | undefined
        if (typeof ResizeObserver !== 'undefined' && navContainerRef.current) {
            resizeObserver = new ResizeObserver(() => updateMaxButtons())
            resizeObserver.observe(navContainerRef.current)
        }

        const handleWindowResize = () => updateMaxButtons()
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleWindowResize)
        }

        return () => {
            resizeObserver?.disconnect()
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleWindowResize)
            }
        }
    }, [maxPageButtonsToShow])

    const disablePrev =
        currentPage === 1 || paginationState.totalPages === 0 || isLoading
    const disableNext =
        paginationState.totalPages === 0 ||
        currentPage >= paginationState.totalPages ||
        isLoading

    return (
        <div
            className={`flex w-full items-center justify-between gap-4 ${textSizeClassName} ${className}`.trim()}
        >
            <div
                ref={navContainerRef}
                className="flex items-center gap-2 flex-1 min-w-0 flex-nowrap"
            >
                <button
                    ref={backButtonRef}
                    type="button"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={disablePrev}
                    className="px-3 py-1 border rounded disabled:opacity-50 whitespace-nowrap"
                >
                    {backLabel}
                </button>
                <div className="flex items-center gap-2 flex-nowrap">
                    {pageNumberButtons.map((page, idx) =>
                        typeof page === 'number' ? (
                            <button
                                key={`${page}-${idx}`}
                                type="button"
                                className={`px-3 py-1 border rounded ${
                                    page === currentPage
                                        ? 'bg-ctxt-blue-light text-white'
                                        : ''
                                }`}
                                onClick={() => onPageChange(page)}
                                disabled={isLoading}
                            >
                                {page}
                            </button>
                        ) : (
                            <span key={`ellipsis-${idx}`} className="px-2">
                                ...
                            </span>
                        ),
                    )}
                </div>
                <button
                    ref={nextButtonRef}
                    type="button"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={disableNext}
                    className="px-3 py-1 border rounded disabled:opacity-50 whitespace-nowrap"
                >
                    {nextLabel}
                </button>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
                <span>
                    {paginationState.rangeStart}-{paginationState.rangeEnd} of{' '}
                    {totalItems}
                </span>
                <div className="flex items-center gap-2">
                    <label htmlFor={selectId} className="whitespace-nowrap">
                        {pageSizeLabel}
                    </label>
                    <select
                        id={selectId}
                        className="border rounded p-1"
                        value={effectivePageSize}
                        onChange={(e) =>
                            handlePageSizeChange(Number(e.target.value))
                        }
                        disabled={isLoading}
                    >
                        {pageSizeOptions.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    )
}

export default PaginationControls
